import EmblaCarousel, { type EmblaOptionsType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import { initWhenVisible } from '../_shared/lazy-init';

( () => {
	const BLOCK_CLASS = 'wp-block-phrase-com-image-carousel';
	const blocks = document.querySelectorAll< HTMLDivElement >( `.${ BLOCK_CLASS }` );
	if ( ! blocks.length ) {
		return;
	}

	const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	blocks.forEach( ( block ) => {
		const rootNode = block.querySelector< HTMLDivElement >( '.embla' );
		if ( ! rootNode ) {
			return;
		}

		const viewportNode = rootNode.querySelector< HTMLDivElement >( `.embla__viewport` );
		if ( ! viewportNode ) {
			return;
		}

		initWhenVisible( block, () => {
			const options: EmblaOptionsType = { loop: true, slidesToScroll: 'auto' };
			const plugins = [
				AutoScroll( {
					// The auto-scroll is started/stopped explicitly below so it only
					// runs while the carousel is on screen.
					playOnInit: false,
					startDelay: 100,
					speed: 1,
				} ),
			];
			const embla = EmblaCarousel( viewportNode, options, plugins );
			const autoScroll = embla.plugins().autoScroll;

			// The auto-scroll runs a requestAnimationFrame loop that reads and writes
			// layout on every frame. Left unchecked it keeps running for the whole
			// page lifetime — even while the carousel is off screen — and competes
			// with every user interaction. Only run it while the block is visible and
			// the tab is in the foreground, and never under a reduced-motion request.
			let isInView = true;
			let isHovered = false;
			let restartTimeout: ReturnType< typeof setTimeout >;

			const resume = (): void => {
				if ( prefersReducedMotion || isHovered || ! isInView || document.hidden ) {
					return;
				}
				autoScroll.play();
			};

			embla.on( 'autoScroll:stop', () => {
				block.classList.add( 'is-paused' );
			} );

			embla.on( 'autoScroll:play', () => {
				block.classList.remove( 'is-paused' );
			} );

			embla.on( 'reInit', () => {
				block.classList.toggle( 'is-paused', ! autoScroll.isPlaying() );
			} );

			// Pause autoplay on hover.
			block.addEventListener( 'mouseenter', () => {
				isHovered = true;
				clearTimeout( restartTimeout );
				autoScroll.stop();
			} );

			block.addEventListener( 'mouseleave', () => {
				isHovered = false;
				clearTimeout( restartTimeout );
				restartTimeout = setTimeout( resume, 2000 );
			} );

			// Pause autoplay when the tab is not visible.
			document.addEventListener( 'visibilitychange', () => {
				if ( document.hidden ) {
					autoScroll.stop();
				} else {
					resume();
				}
			} );

			// Pause autoplay when the carousel is scrolled out of the viewport.
			if ( prefersReducedMotion ) {
				// Never auto-scroll — keep the carousel usable via drag / arrows.
			} else if ( typeof IntersectionObserver !== 'undefined' ) {
				new IntersectionObserver(
					( entries ) => {
						for ( const entry of entries ) {
							isInView = entry.isIntersecting;
							if ( isInView ) {
								resume();
							} else {
								autoScroll.stop();
							}
						}
					},
					{ threshold: 0 }
				).observe( block );
			} else {
				// No IntersectionObserver: can't gate on visibility, just play.
				resume();
			}

			const prevButtonNode = rootNode.querySelector< HTMLButtonElement >( `.embla__prev` );
			const nextButtonNode = rootNode.querySelector< HTMLButtonElement >( `.embla__next` );

			if ( prevButtonNode && nextButtonNode ) {
				prevButtonNode.addEventListener( 'click', embla.scrollPrev, false );
				nextButtonNode.addEventListener( 'click', embla.scrollNext, false );
			}
		} );
	} );
} )();
